import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import IfCondition from 'components/shared/IfCondition';
import TextFieldControlled from 'components/shared/TextFieldControlled';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import useAddressesSWR from '../../../hooks/addresses/useAddresses';
import useQuotationActionsDialog from '../../../hooks/dialogs/useQuotationActionsDialog';
import useMyQuotations from '../../../hooks/quotations/useMyQuotations';
import useFullScreenLoading from '../../../hooks/useFullScreenLoading';
import { DialogComponentProps } from '../../../models/quotations/NewQutationDialogProps';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { isNullOrUndefined } from '../../../utils/utils';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import { ErrorTypography } from '../../styled/ErrorTypography';

const schema = Yup.object({
    shipping_checkbox: Yup.boolean().oneOf([true, false]),
    project_number: Yup.string()
        .optional()
        .test(
            'project-number-checkbox-check',
            'A projektszám megadása kötelező!',
            function (value): boolean {
                const { shipping_checkbox } = this.parent;
                if (!shipping_checkbox) return true;
                return value?.length > 1;
            }
        ),
    shipping: Yup.string()
        .optional()
        .test(
            'shipping-checkbox-check',
            'A szállítási megadása kötelező!',
            function (value): boolean {
                const { shipping_checkbox } = this.parent;
                if (!shipping_checkbox) return true;
                return !!value?.length;
            }
        ),
    note: Yup.string().optional(),
});

const CreateNewMyQuotation: React.FC<DialogComponentProps> = (props) => {
    const { products, handleSave } = props;

    if (isNullOrUndefined(props)) return null;

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATION_DIALOG.NEW_QUOTATION_DIALOG',
    });

    const myQuotations = useMyQuotations();

    const [saving, setSaving] = useState(false);

    const { addresses, isLoading, isError } = useAddressesSWR();

    const form = useForm<FormValues>({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            shipping_checkbox: false,
            note: '',
            shipping: null,
            project_number: '',
        },
    });

    const shippingCheckbox = form.watch('shipping_checkbox');

    const quotationActionsDialog = useQuotationActionsDialog();

    const fullScreenLoading = useFullScreenLoading();

    const closeConfirmDialog = () => {
        form.reset();
        quotationActionsDialog.closeDialog();
    };

    const handleCreatedSuccessfully = (quotationId) => {
        form.reset();
        SnackbarUtils.success(t('CREATED_SUCCESSFULLY').toString());
        fullScreenLoading.setFullScreenLoading(false);
        handleSave(quotationId);
    };

    const onConfirmDialog: SubmitHandler<FormValues> = (data) => {
        setSaving(true);
        fullScreenLoading.setFullScreenLoading(true);
        myQuotations
            .createQuotation({
                salesQuoteLines:
                    myQuotations.convertProductsToQuotation(products),
                sourceName: 'SZERELOI',
                projectName: data.project_number ? data.project_number : null,
                shipTo: data.shipping_checkbox ? data.shipping : null,
                note: data.note,
            })
            .then((resp) => {
                handleCreatedSuccessfully(resp.quote.quoteID);
            })
            .finally(() => {
                setSaving(false);
                fullScreenLoading.setFullScreenLoading(false);
            });
    };

    useEffect((): void => {
        if (isNullOrUndefined(addresses) || addresses.length < 1) return;
        form.setValue('shipping', addresses[0].id.toString(), {
            shouldValidate: true,
        });
    }, [form.setValue, addresses]);

    return (
        <>
            <form onSubmit={form.handleSubmit(onConfirmDialog)}>
                <DialogContent>
                    <Stack gap={2}>
                        <Typography>{t('CHANGE_SHIPPING')}</Typography>

                        <FormControl fullWidth>
                            <TextFieldControlled
                                label={t('NOTE')}
                                multiline
                                rows={3}
                                control={form.control}
                                name="note"
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <FormControlLabel
                                control={
                                    <Controller
                                        control={form.control}
                                        name="shipping_checkbox"
                                        render={({
                                            field: { value, onBlur, onChange },
                                        }) => (
                                            <Checkbox
                                                value={value}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                            />
                                        )}
                                    />
                                }
                                label={
                                    <Typography>
                                        {t('CHANGE_SHIPPING_LABEL')}
                                    </Typography>
                                }
                            />
                        </FormControl>

                        {shippingCheckbox && (
                            <Stack gap={2}>
                                {!isLoading && addresses && (
                                    <Controller
                                        control={form.control}
                                        name="shipping"
                                        render={({
                                            field: { value, onBlur, onChange },
                                            fieldState: { error },
                                        }) => (
                                            <TextField
                                                label={t('SHIPPING_LABEL')}
                                                error={!!error}
                                                helperText={error?.message}
                                                select
                                                value={value}
                                                onBlur={onBlur}
                                                onChange={(event) =>
                                                    onChange(event.target.value)
                                                }
                                            >
                                                {addresses.map((address) => (
                                                    <MenuItem
                                                        key={address.id}
                                                        value={address.id}
                                                    >
                                                        {address.shippingAddress
                                                            .city +
                                                            ' ' +
                                                            address
                                                                .shippingAddress
                                                                .address1}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                )}

                                <IfCondition condition={isError}>
                                    <ErrorTypography
                                        errorCode={
                                            'NEW_QUOTATION_ADDRESS_LOADING_ERROR'
                                        }
                                    />
                                </IfCondition>

                                <FormControl fullWidth>
                                    <TextFieldControlled
                                        label={t('PROJECT_NUMBER')}
                                        control={form.control}
                                        name="project_number"
                                    />
                                </FormControl>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Stack flexDirection="row" gap={2}>
                        <Button
                            size="small"
                            color="error"
                            onClick={closeConfirmDialog}
                        >
                            {t('CANCEL')}
                        </Button>

                        <PrimaryBtn
                            size="small"
                            type="submit"
                            disabled={saving || isLoading}
                        >
                            {t('CONFIRM')}
                        </PrimaryBtn>
                    </Stack>
                </DialogActions>
            </form>
        </>
    );
};

export default CreateNewMyQuotation;

type FormValues = {
    shipping_checkbox: boolean;
    project_number: string;
    shipping: string;
    note?: string;
};
